import { useNavigate } from '@tanstack/react-router';
import type { SchoolLevel } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useRoleNewsFeed } from '@/lib/news/use-role-news-feed';
import { useFeedVisit } from '@/lib/news/feed-seen';
import { isFeedItemUnread } from '@/lib/news/feed-unread';
import { useStudentProfile } from '@/hooks/use-student-profile';
import { useLaunchPending } from '@/hooks/use-launch-pending';
import { NewsFeed } from '@/components/news/news-feed';
import { NewsTabs } from '@/features/news-tabs/news-tabs';
import { FeedRefreshButton } from '@/features/news-refresh/feed-refresh-button';
import { PullToRefreshZone } from '@/features/news-refresh/pull-to-refresh-zone';
import { useFeedAutoRefresh } from '@/features/news-refresh/use-feed-auto-refresh';
import type { AssignmentRow, FeedItem } from '@/components/news/types';

export function StudentNewsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pendingId, launch } = useLaunchPending();
  const userId = user?.id ?? '';

  // Niveau de l'élève (fixe) → alimente les onglets Live School / Classements.
  const { data: studentProfile } = useStudentProfile(userId);
  const level = (studentProfile?.level as SchoolLevel | null | undefined) ?? null;

  const {
    items,
    authorsById,
    doneAssignmentKeys,
    isLoading,
    hasMore,
    isFetchingMore,
    loadMore,
    refresh,
    isRefreshing,
  } = useRoleNewsFeed('student');
  const seenAtAtOpen = useFeedVisit();
  const isUnread = (item: FeedItem) =>
    seenAtAtOpen !== undefined && isFeedItemUnread(item, seenAtAtOpen, userId);

  // Spinner sur le bouton du devoir jusqu'à l'ouverture de l'exercice.
  function goToExercise(a: AssignmentRow) {
    const chapter = a.exercises?.chapters;
    if (!chapter || !a.exercises) return;
    void launch(a.id, () => {
      void navigate({
        to: '/learn/$subject/$level/$chapter/$exercise',
        params: {
          subject: chapter.subject,
          level: chapter.level,
          chapter: chapter.slug,
          exercise: a.exercise_id,
        },
      });
    }).catch(console.error);
  }

  useFeedAutoRefresh(refresh);

  return (
    <div className="pb-safe mx-auto max-w-2xl px-4 py-6 sm:py-8">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-ink-900 text-2xl font-extrabold">Feed</h1>
          <FeedRefreshButton onRefresh={refresh} isRefreshing={isRefreshing} />
        </div>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          Tes devoirs, le Live School de ton niveau et les classements.
        </p>
      </div>

      <NewsTabs
        userId={userId}
        level={level}
        actus={
          <PullToRefreshZone onRefresh={refresh} isRefreshing={isRefreshing}>
            <NewsFeed
              items={items}
              isLoading={isLoading}
              hasMore={hasMore}
              isFetchingMore={isFetchingMore}
              onLoadMore={loadMore}
              viewerRole="student"
              viewerId={userId}
              authorsById={authorsById}
              isUnread={isUnread}
              doneAssignmentKeys={doneAssignmentKeys}
              onStartAssignment={goToExercise}
              startingAssignmentId={pendingId}
            />
          </PullToRefreshZone>
        }
      />
    </div>
  );
}
